import axiosInstance from '../../services/axiosInstance';
import {
  IListningarEntry,
  IListningarEntriesResponse,
  IListningarCategory,
  IListningarStatsResponse,
  IListningarMedia,
  IListningarReactionSummary,
  IListningarEntryFormData,
} from './types';

// =============================================
// Categories
// =============================================

export async function fetchCategories(): Promise<IListningarCategory[]> {
  const { data } = await axiosInstance.get<IListningarCategory[]>('/api/listningar/categories');
  return data;
}

export async function createCategory(cat: { id: string; label: string; color: string }): Promise<IListningarCategory> {
  const { data } = await axiosInstance.post<IListningarCategory>('/api/listningar/categories', cat);
  return data;
}

export async function updateCategory(id: string, updates: { label?: string; color?: string; order?: number }): Promise<IListningarCategory> {
  const { data } = await axiosInstance.put<IListningarCategory>(`/api/listningar/categories/${id}`, updates);
  return data;
}

export async function deleteCategory(id: string): Promise<void> {
  await axiosInstance.delete(`/api/listningar/categories/${id}`);
}

export async function reorderCategories(order: { id: string; order: number }[]): Promise<IListningarCategory[]> {
  const { data } = await axiosInstance.put<IListningarCategory[]>('/api/listningar/categories/reorder', { order });
  return data;
}

// =============================================
// Current week
// =============================================

export async function fetchCurrentWeek(): Promise<{ weekNumber: number; year: number }> {
  const { data } = await axiosInstance.get<{ weekNumber: number; year: number }>('/api/listningar/current-week');
  return data;
}

// =============================================
// Entries CRUD
// =============================================

export interface FetchEntriesParams {
  category?: string;
  search?: string;
  year?: number;
  weekNumber?: number;
  limit?: number;
  offset?: number;
}

export async function fetchEntries(params?: FetchEntriesParams): Promise<IListningarEntriesResponse> {
  const { data } = await axiosInstance.get<IListningarEntriesResponse>('/api/listningar/entries', { params });
  return data;
}

export async function fetchEntry(id: string): Promise<IListningarEntry> {
  const { data } = await axiosInstance.get<IListningarEntry>(`/api/listningar/entries/${id}`);
  return data;
}

export async function createEntry(entryData: IListningarEntryFormData): Promise<IListningarEntry> {
  const { data } = await axiosInstance.post<IListningarEntry>('/api/listningar/entries', entryData);
  return data;
}

export async function updateEntry(id: string, entryData: Partial<IListningarEntryFormData>): Promise<IListningarEntry> {
  const { data } = await axiosInstance.put<IListningarEntry>(`/api/listningar/entries/${id}`, entryData);
  return data;
}

export async function deleteEntry(id: string): Promise<void> {
  await axiosInstance.delete(`/api/listningar/entries/${id}`);
}

// =============================================
// Reactions
// =============================================

export async function toggleReaction(entryId: string, type: string): Promise<{ reactions: any[]; summary: IListningarReactionSummary }> {
  const { data } = await axiosInstance.post(`/api/listningar/entries/${entryId}/reactions`, { type });
  return data;
}

// =============================================
// Media upload
// =============================================

export async function uploadMedia(files: File[], onProgress?: (percent: number) => void): Promise<IListningarMedia[]> {
  const formData = new FormData();
  files.forEach(file => formData.append('media', file));

  const { data } = await axiosInstance.post<{ media: IListningarMedia[] }>('/api/listningar/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percent);
      }
    },
  });
  return data.media;
}

// =============================================
// Custom thumbnail upload
// =============================================

export async function uploadThumbnail(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('thumbnail', file);
  const { data } = await axiosInstance.post<{ thumbnailUrl: string }>('/api/listningar/upload-thumbnail', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.thumbnailUrl;
}

// =============================================
// Stats
// =============================================

export async function fetchStats(year?: number, month?: number): Promise<IListningarStatsResponse> {
  const params: Record<string, number> = {};
  if (year) params.year = year;
  if (month) params.month = month;
  const { data } = await axiosInstance.get<IListningarStatsResponse>('/api/listningar/stats', { params });
  return data;
}
